import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Database } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Donut } from '@/components/charts/donut';
import type { AnswerData } from '@/lib/assistant';

/**
 * Tool results drawn as components rather than retold as prose.
 *
 * The model decides what to look up; the app draws what came back. That is worth
 * more than the prettier layout it produces: a number on screen is the query's
 * number, not the model's recollection of it, so this is one fewer place an
 * answer can drift from the database.
 *
 * A shape with no renderer here falls back to the model's sentence, which is what
 * the panel did for everything until now — an unknown tool loses the picture, not
 * the answer.
 */

type Row = Record<string, unknown>;

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/**
 * The frame every rendered answer sits in — and the box its charts measure
 * themselves against. Declaring the container here rather than at each call
 * site means a card laid out inside a narrow dashboard column adapts on its
 * own, wherever it is drawn: an answer, a widget, the briefing or the side
 * panel. Anything else would work only where someone remembered to opt in.
 */
function Shell({ children, footer }: { children: React.ReactNode; footer?: string }) {
  return (
    <div className="@container mt-2 overflow-hidden rounded-lg border border-border bg-muted/20">
      <div className="px-3 py-2.5">{children}</div>
      {footer && (
        <p className="flex items-center gap-1.5 border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
          <Database className="h-3 w-3 shrink-0" />
          {footer}
        </p>
      )}
    </div>
  );
}

/** What the bars are counting, said plainly under them. */
function footerFor(result: Row, groupCount: number): string {
  const by = str(result.groupBy) ?? 'group';
  // A share chart plots percentages, so the total it is a share *of* is the one
  // number the bars no longer carry.
  if (str(result.unit) === '%' && num(result.totalHours) !== null) {
    return `share of ${num(result.totalHours)} hours · by ${by}`;
  }
  if (num(result.totalHours) !== null) return `${num(result.totalHours)} hours · by ${by}`;
  // Salary stats chart the average, which is a very different number from a
  // headcount and must not be mistaken for one.
  if (Array.isArray(result.byCurrency)) return `average pay · by ${by}`;
  return `${num(result.total) ?? groupCount} people · grouped by ${by}`;
}

/** Counts as bars. Widths are relative to the largest group, not to the total —
 *  the question is "how do these compare", and a share-of-total bar for a long
 *  tail of small departments is unreadable. */
/**
 * One labelled bar chart. Shared by the single- and multi-panel renderers.
 *
 * `unit` changes what the bars mean. Without it the widths are relative to the
 * largest group, because the question is "how do these compare" and a
 * share-of-total bar for a long tail of small departments is unreadable. A
 * percentage is the opposite: the whole is the point, so the bars are drawn
 * against 100 and the row reads 38.2% rather than a bare 38.2.
 */
function BarPanel({ title, groups, unit }: { title?: string; groups: Row[]; unit?: string }) {
  if (groups.length === 0) return null;
  const isShare = unit === '%';
  const top = isShare ? 100 : Math.max(...groups.map((g) => num(g.count) ?? 0), 1);

  return (
    <div>
      {title && (
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </p>
      )}
      <div className="-mx-1.5">
        {groups.map((g, i) => {
          const count = num(g.count) ?? 0;
          const hours = num(g.hours);
          return (
            /* The row is the hit target, not the painted bar: a 8px bar is a
               pinpoint, and the reader aims at the row they are reading. It
               lifts on hover so the chart visibly responds. */
            <div
              key={i}
              tabIndex={0}
              className="group relative grid grid-cols-[5.5rem_1fr_3rem] items-center gap-x-2 rounded-md px-1.5 py-1 outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
            >
              <span className="truncate text-[11px] text-muted-foreground">{str(g.name) ?? '—'}</span>
              {/* Square where it starts, rounded only at the data end — the
                  shape says which end is the baseline and which is the value. */}
              <span className="h-2 overflow-hidden rounded-[1px] bg-muted">
                <span
                  className="block h-full rounded-r-[4px] bg-hygge transition-[width] duration-300"
                  style={{ width: `${Math.max((count / top) * 100, 2)}%` }}
                />
              </span>
              <span className="text-right text-[11px] font-medium tabular-nums text-foreground">
                {count}
                {unit ?? ''}
              </span>

              {/* The readout leads with the value, because the reader already
                  has the category — they came here for the number. */}
              <span
                role="tooltip"
                className="pointer-events-none absolute -top-1 left-[5.5rem] z-10 hidden -translate-y-full whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-[11px] shadow-md group-hover:block group-focus-visible:block"
              >
                <span className="font-semibold tabular-nums text-foreground">
                  {count}
                  {unit ?? ''}
                </span>{' '}
                <span className="text-muted-foreground">{str(g.name) ?? '—'}</span>
                {/* A share chart charts percentages; the hours behind one are
                    the thing the bar can no longer say. */}
                {unit === '%' && hours !== null && (
                  <span className="text-muted-foreground"> · {hours} h</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Findings, ranked, each carrying the number that justifies it.
 *
 * Deliberately not a chart. These are judgements about single subjects — this
 * project, this person — and the reader's next question is always "why", which
 * a bar cannot answer. The number stays prominent so the judgement can be
 * disagreed with on the evidence rather than taken on trust.
 *
 * Status colour is reserved for status and never reused as a series hue, and it
 * never travels alone: each row carries the severity as a word beside the bar,
 * so the ranking survives colour blindness and a greyscale print.
 */
const SEVERITY: Record<string, { bar: string; label: string; word: string }> = {
  critical: { bar: 'bg-destructive', label: 'text-destructive', word: 'Critical' },
  warning: { bar: 'bg-amber-500', label: 'text-amber-600 dark:text-amber-500', word: 'Watch' },
  info: { bar: 'bg-hygge', label: 'text-muted-foreground', word: 'Note' },
};

function Insights({ result }: { result: Row }) {
  const findings = (Array.isArray(result.findings) ? result.findings : []) as Row[];
  const summary = (Array.isArray(result.summary) ? result.summary : []) as Row[];
  const note = str(result.note);

  // The portfolio's shape first, the exceptions after it. A reader who opens
  // with four red cards and no totals cannot tell whether two loss-making
  // projects out of ten is a crisis or a rounding error.
  const tiles = summary.length > 0 && (
    <div className="mb-3 grid gap-2 @sm:grid-cols-2 @2xl:grid-cols-4">
      {summary.map((s, i) => (
        <Metric key={i} label={str(s.label) ?? ''} value={str(s.value) ?? '—'} />
      ))}
    </div>
  );

  if (findings.length === 0) {
    return (
      <Shell>
        {tiles}
        <p className="text-[11px] text-muted-foreground">
          {note ?? 'Nothing stands out right now.'}
        </p>
      </Shell>
    );
  }

  return (
    <Shell footer={`${findings.length} findings · ranked by severity`}>
      {tiles}
      <ul className="space-y-1.5">
        {findings.map((f, i) => {
          const tone = SEVERITY[str(f.severity) ?? 'info'] ?? SEVERITY.info;
          return (
            <li
              key={i}
              className="flex gap-2.5 rounded-md border border-border bg-card/60 p-2.5"
            >
              <span className={cn('mt-0.5 w-0.5 shrink-0 rounded-full', tone.bar)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-[12px] font-medium text-foreground">
                    {str(f.title) ?? '—'}
                  </span>
                  <span className="shrink-0 text-[11px] font-semibold tabular-nums text-foreground">
                    {str(f.metric) ?? ''}
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  {str(f.detail) ?? ''}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <span className={cn('font-medium uppercase tracking-wide', tone.label)}>
                    {tone.word}
                  </span>
                  <span aria-hidden>·</span>
                  <span>{str(f.area) ?? ''}</span>
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}

/**
 * Money per project per month, as a grid.
 *
 * A heatmap is the right form here and a bar chart is not: two dimensions carry
 * meaning at once — which project, which month — and the reader's question is
 * "where is the bad cell", which a grid answers by eye.
 *
 * The scale is chosen by what the number *is*, not by taste. Revenue is a
 * magnitude, so it gets one hue, light to dark. Margin has a real zero — profit
 * on one side, loss on the other — so it gets a diverging pair about a neutral
 * grey; a single ramp would render a −9% and a +9% as near-identical pale cells
 * and hide the only thing worth seeing.
 *
 * A month with no roster entry is drawn as an empty cell, not a zero. "Nobody
 * worked on this" and "worked and billed nothing" are different facts, and
 * colouring both as zero would quietly assert the second.
 */
function Heatmap({ result }: { result: Row }) {
  // Declared before any early return — a hook behind a conditional throws the
  // moment the data empties out.
  const [reading, setReading] = useState<string | null>(null);

  const rows = (Array.isArray(result.rows) ? result.rows : []) as Row[];
  const months = (Array.isArray(result.months) ? result.months : []) as string[];
  const isMargin = str(result.metric) !== 'revenue';
  if (rows.length === 0 || months.length === 0) {
    return (
      <Shell>
        <p className="text-[11px] text-muted-foreground">
          {str(result.note) ?? 'No roster data for that period.'}
        </p>
      </Shell>
    );
  }

  // The sign is handled by its own colours, so the blue ramp only ever carries
  // the healthy values — and it is stretched across the range those actually
  // occupy, not across 0..max. Anchored at zero, a portfolio whose margins all
  // sit between 24% and 50% spends the bottom half of the scale on values that
  // never occur and paints every project the same blue.
  const ramped = rows
    .flatMap((r) =>
      ((Array.isArray(r.cells) ? r.cells : []) as Row[])
        .map((c) => num(c.value))
        .filter((v): v is number => v !== null),
    )
    .filter((v) => !isMargin || v >= 15);
  const lo = ramped.length ? Math.min(...ramped) : 0;
  const hi = ramped.length ? Math.max(...ramped) : 1;

  /** The step a value lands on, as the pair of tokens it ships with. */
  const step = (v: number): { bg: string; fg: string } => {
    if (isMargin) {
      if (v < 0) return { bg: 'var(--heat-loss)', fg: 'var(--heat-loss-ink)' };
      if (v < 8) return { bg: 'var(--heat-thin)', fg: 'var(--heat-thin-ink)' };
      if (v < 15) return { bg: 'var(--heat-zero)', fg: 'var(--heat-zero-ink)' };
    }
    // A flat portfolio has no spread to show; one mid tone is honest, and
    // dividing by nothing is not.
    const share = hi > lo ? (v - lo) / (hi - lo) : 0.5;
    const n = share > 0.75 ? 4 : share > 0.5 ? 3 : share > 0.25 ? 2 : 1;
    return { bg: `var(--heat-${n})`, fg: `var(--heat-${n}-ink)` };
  };

  const label = (m: string) =>
    new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short' });
  const cash = (n: number) => (n >= 1000 ? `$${Math.round(n / 100) / 10}k` : `$${n}`);
  const read = (v: number) => (isMargin ? `${v}%` : cash(v));

  // What a colour means, said once. The cells carry their own numbers, so this
  // is not load-bearing for reading a single cell — it is what tells you which
  // direction across the grid is the good one.
  const key = isMargin
    ? [
        { bg: 'var(--heat-loss)', text: 'loss' },
        { bg: 'var(--heat-thin)', text: 'thin' },
        { bg: 'var(--heat-2)', text: 'healthy' },
        { bg: 'var(--heat-4)', text: 'strong' },
      ]
    : [
        { bg: 'var(--heat-1)', text: 'less' },
        { bg: 'var(--heat-2)', text: '' },
        { bg: 'var(--heat-3)', text: '' },
        { bg: 'var(--heat-4)', text: 'more billed' },
      ];

  return (
    <Shell
      footer={
        isMargin
          ? 'margin per project · month by month'
          : `${cash(num(result.totalRevenue) ?? 0)} billed · by project and month`
      }
    >
      {/* One readout above the grid rather than a tooltip per cell. A floating
          tooltip would be clipped by the scroll container the wide grid needs —
          `overflow-x: auto` forces the vertical axis to clip too — and the top
          row's readout is exactly the one that would vanish. Fixed height so
          the grid does not jump as the pointer moves. */}
      <p className="mb-1.5 flex h-4 items-center gap-1.5 text-[11px]">
        {reading ? (
          <span className="truncate text-muted-foreground">{reading}</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {isMargin ? 'Margin' : 'Revenue'} · hover a cell
          </span>
        )}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-[2px] text-[11px]">
          <thead>
            <tr>
              {/* The label column grows with the card, not the window. Fixed at
                  the dashboard-column width, opening a widget full size gave
                  more room to the cells and still truncated every name — which
                  is the one thing expanding it was for. */}
              <td className="w-[7rem] @xl:w-[12rem] @3xl:w-[18rem]" />
              {months.map((m) => (
                <th
                  key={m}
                  scope="col"
                  className="px-1 pb-0.5 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {label(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const cells = (Array.isArray(r.cells) ? r.cells : []) as Row[];
              const name = str(r.name) ?? '—';
              return (
                <tr key={i}>
                  <th
                    scope="row"
                    className="max-w-[7rem] truncate pr-2 text-left text-[11px] font-normal text-muted-foreground @xl:max-w-[12rem] @3xl:max-w-[18rem]"
                    title={name}
                  >
                    {name}
                  </th>
                  {cells.map((c, j) => {
                    const v = num(c.value);
                    const rev = num(c.revenue);
                    const cost = num(c.cost);
                    const tone = v === null ? null : step(v);
                    // Value first: the reader already knows which row and column
                    // they are pointing at. The money underneath a margin is the
                    // part the cell itself can no longer say.
                    const detail =
                      v === null
                        ? `${name} · ${label(str(c.month) ?? '')} — no hours logged`
                        : `${read(v)} · ${name} · ${label(str(c.month) ?? '')}` +
                          (isMargin && rev !== null && cost !== null
                            ? ` · ${cash(rev)} billed, ${cash(cost)} cost`
                            : '');
                    return (
                      <td key={j} className="p-0">
                        <div
                          tabIndex={0}
                          title={detail}
                          onMouseEnter={() => setReading(detail)}
                          onMouseLeave={() => setReading(null)}
                          onFocus={() => setReading(detail)}
                          onBlur={() => setReading(null)}
                          className="flex h-7 min-w-[3.25rem] items-center justify-center rounded-[3px] font-medium tabular-nums outline-none ring-offset-2 ring-offset-background transition-[filter,box-shadow] hover:brightness-[1.08] focus-visible:ring-2 focus-visible:ring-ring"
                          style={
                            tone
                              ? { background: tone.bg, color: tone.fg }
                              : {
                                  // An absent month is drawn as an outline, never
                                  // a fill — it must read as "no data", not as a
                                  // low value.
                                  border: '1px dashed hsl(var(--border))',
                                  color: 'hsl(var(--muted-foreground))',
                                }
                          }
                        >
                          {v === null ? '·' : read(v)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
        {key.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-[2px]" style={{ background: k.bg }} />
            {k.text}
          </span>
        ))}
      </div>
    </Shell>
  );
}

/**
 * The company as a reporting tree.
 *
 * Drawn as an indented tree with connector lines rather than the classic
 * box-and-line chart. A top-down chart is the more familiar picture, but at
 * forty people it is wider than any screen and unreadable in a dashboard
 * column; indentation carries the same hierarchy at any width, and reads the
 * same in a narrow widget as it does full size.
 *
 * Each node carries the size of the branch under it, which is the number that
 * says how much of the company a person is responsible for — a detail a plain
 * name-and-title tree loses.
 */
/** One person, as a card. Fixed height so the connectors can meet it exactly. */
function OrgBox({ node, lead }: { node: Row; lead?: boolean }) {
  const name = str(node.name) ?? '—';
  const avatar = str(node.avatarUrl);
  const team = num(node.teamSize) ?? 0;
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('');

  return (
    <div
      className={cn(
        // Narrow on purpose. Eleven of these plus their gutters set how wide the
        // whole diagram is, and the difference between fitting a screen and
        // overflowing it by a column is entirely here.
        'flex h-11 w-40 shrink-0 items-center gap-2 rounded-lg border px-2',
        lead ? 'border-hygge/40 bg-hygge/5' : 'border-border bg-card',
      )}
      title={[name, str(node.position), str(node.department)].filter(Boolean).join(' · ')}
    >
      {avatar ? (
        <img src={avatar} alt="" className="h-6 w-6 shrink-0 rounded-full bg-muted" />
      ) : (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground">
          {initials}
        </span>
      )}
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[11px] font-medium text-foreground">{name}</span>
        <span className="block truncate text-[10px] text-muted-foreground">
          {str(node.position) ?? str(node.department) ?? ''}
        </span>
      </span>
      {team > 0 && (
        <span className="shrink-0 rounded bg-muted px-1 text-[9px] tabular-nums text-muted-foreground">
          {team}
        </span>
      )}
    </div>
  );
}

/**
 * A subtree.
 *
 * The top level branches sideways and everything below it stacks downward. That
 * split is not cosmetic: laid out sideways the whole way, forty people are
 * wider than any screen, and stacked the whole way it stops being a diagram and
 * becomes a list. Departments fan out; the people inside one hang off a spine.
 */
function OrgBranch({ node, depth }: { node: Row; depth: number }) {
  const reports = (Array.isArray(node.reports) ? node.reports : []) as Row[];
  if (reports.length === 0) return <OrgBox node={node} />;

  if (depth > 0) {
    return (
      <div className="flex flex-col items-start">
        <OrgBox node={node} />
        <div className="mt-1.5 pl-6">
          {reports.map((r, i) => {
            const last = i === reports.length - 1;
            return (
              <div key={i} className="relative pt-1.5">
                {/* The spine stops at the last elbow instead of running into
                    empty space, which is what makes the branch look finished. */}
                <span
                  aria-hidden
                  className="absolute -left-3 top-0 w-px bg-border"
                  style={{ height: last ? '1.75rem' : '100%' }}
                />
                <span aria-hidden className="absolute -left-3 top-[1.75rem] h-px w-3 bg-border" />
                <OrgBranch node={r} depth={depth + 1} />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <OrgBox node={node} lead />
      <span aria-hidden className="h-5 w-px bg-border" />
      <div className="flex items-start">
        {reports.map((r, i) => (
          <div key={i} className="relative flex flex-col items-center px-1.5">
            {/* Half a bus each side, so the run stops at the outermost stubs
                rather than overhanging the first and last columns. */}
            <span
              aria-hidden
              className="absolute top-0 h-px bg-border"
              style={{
                left: i === 0 ? '50%' : 0,
                right: i === reports.length - 1 ? '50%' : 0,
              }}
            />
            <span aria-hidden className="h-5 w-px bg-border" />
            <OrgBranch node={r} depth={1} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Below this the names stop being readable, and a chart you cannot read is
 *  worse than one you have to scroll. */
const MIN_ORG_SCALE = 0.72;

function OrgChart({ result }: { result: Row }) {
  const viewport = useRef<HTMLDivElement>(null);
  const diagram = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ scale: 1, width: 0, height: 0 });

  // Scale the whole diagram down until it fits the space it has been given.
  // Scrolling a chart that is only a little too wide slices the outer columns
  // through the middle of a card, which reads as broken rather than as "there
  // is more over there".
  //
  // Transforms do not change layout metrics, so these measurements report the
  // diagram's natural size even while it is being scaled — no need to reset the
  // transform to measure.
  useLayoutEffect(() => {
    const box = viewport.current;
    const inner = diagram.current;
    if (!box || !inner) return;

    const measure = () => {
      const natural = inner.scrollWidth;
      if (!natural) return;
      const scale = Math.min(1, Math.max(MIN_ORG_SCALE, box.clientWidth / natural));
      const next = { scale, width: natural * scale, height: inner.offsetHeight * scale };
      // Compare before setting: this runs from a ResizeObserver, and a state
      // update per callback with no change is a loop waiting to happen.
      setFit((prev) =>
        prev.scale === next.scale && prev.width === next.width && prev.height === next.height
          ? prev
          : next,
      );
    };

    measure();
    // Both are watched: the container because the panel resizes around it, and
    // the diagram because its own width can settle after first layout — a
    // webfont arriving is enough. This cannot feed back, because the diagram is
    // `w-max` and takes its width from its content, never from the wrapper.
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [result]);

  // Whatever still does not fit opens centred: at scroll zero you get the left
  // edge, not the top of the company, which is where reading a tree starts.
  useEffect(() => {
    const box = viewport.current;
    if (box) box.scrollLeft = (box.scrollWidth - box.clientWidth) / 2;
  }, [fit]);

  const roots = (Array.isArray(result.roots) ? result.roots : []) as Row[];
  if (roots.length === 0) {
    return (
      <Shell>
        <p className="text-[11px] text-muted-foreground">
          {str(result.note) ?? 'No reporting structure to draw.'}
        </p>
      </Shell>
    );
  }

  const total = num(result.total) ?? 0;
  const levels = num(result.levels) ?? 0;
  const departments = (Array.isArray(result.departments) ? result.departments : []) as Row[];

  return (
    <Shell
      footer={`${total} people · ${levels} level${levels === 1 ? '' : 's'}${
        departments.length ? ` · ${departments.length} departments` : ''
      }`}
    >
      {/* A chart with people missing from it says so, above the drawing rather
          than under it — otherwise the tree looks complete and simply is not. */}
      {str(result.note) && (
        <p className="mb-2 rounded border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
          {str(result.note)}
        </p>
      )}

      {/* The diagram is wider than the card by design — a company does not fold
          into 500px. It scrolls inside its own box so the page never does. */}
      <div ref={viewport} className="overflow-x-auto pb-1">
        {/* The wrapper carries the scaled size so the layout matches what is on
            screen — without it the box keeps the diagram's full height and
            leaves a band of empty space under a shrunken chart. */}
        <div
          className="mx-auto"
          style={fit.width ? { width: fit.width, height: fit.height } : undefined}
        >
          <div
            ref={diagram}
            className="flex w-max origin-top-left items-start gap-6"
            style={{ transform: `scale(${fit.scale})` }}
          >
            {roots.map((r, i) => (
              <OrgBranch key={i} node={r} depth={0} />
            ))}
          </div>
        </div>
      </div>

      {departments.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-2">
          {departments.map((d, i) => (
            <span
              key={i}
              className="rounded border border-border px-1.5 py-px text-[10px] text-muted-foreground"
            >
              {str(d.name) ?? '—'} <span className="tabular-nums text-foreground">{num(d.count)}</span>
            </span>
          ))}
        </div>
      )}
    </Shell>
  );
}

/** A headline number with its label — for the figures a chart cannot carry. */
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-base font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}

/**
 * A result carrying several charts at once.
 *
 * Some questions are genuinely more than one picture — vacation patterns are a
 * department breakdown, a seasonal shape and a length distribution, and showing
 * them apart is what makes them comparable. The tool decides how many panels
 * there are and what each is called; this only draws them.
 */
function MultiChart({ result }: { result: Row }) {
  const charts = (Array.isArray(result.charts) ? result.charts : []) as Row[];
  if (charts.length === 0) return null;

  const metrics: [string, string][] = [];
  if (num(result.totalRequests) !== null) metrics.push(['Absences', String(num(result.totalRequests))]);
  if (num(result.totalDays) !== null) metrics.push(['Days off', String(num(result.totalDays))]);
  if (num(result.averageLength) !== null) metrics.push(['Average', `${num(result.averageLength)} d`]);
  if (num(result.medianLength) !== null) metrics.push(['Median', `${num(result.medianLength)} d`]);

  const months = num(result.windowMonths);

  return (
    <Shell footer={months ? `last ${months} months · approved absences only` : undefined}>
      {/* Sized against the card, not the window: the same three panels are a
          row across a full-width answer and a stack inside a dashboard column,
          and a viewport breakpoint cannot tell those two apart. */}
      {metrics.length > 0 && (
        <div className="mb-3 grid gap-2 @sm:grid-cols-2 @2xl:grid-cols-4">
          {metrics.map(([label, value]) => (
            <Metric key={label} label={label} value={value} />
          ))}
        </div>
      )}
      <div className="grid gap-4 @md:grid-cols-2 @3xl:grid-cols-3">
        {charts.map((c, i) => (
          <BarPanel
            key={i}
            title={str(c.title) ?? undefined}
            groups={(Array.isArray(c.groups) ? c.groups : []) as Row[]}
          />
        ))}
      </div>
    </Shell>
  );
}

/**
 * A single breakdown — headcount by department, hours by project, and the
 * others that return one list of groups. It draws through BarPanel rather than
 * repeating the bar markup, so a unit added on one chart is understood by all
 * of them.
 */
function Headcount({ result }: { result: Row }) {
  const groups = (Array.isArray(result.groups) ? result.groups : []) as Row[];
  if (groups.length === 0) return null;
  const unit = str(result.unit) ?? undefined;

  return (
    <Shell footer={footerFor(result, groups.length)}>
      {/* Percentages of one total are a composition — that is what a ring is
          for. Everything else is magnitude, which bars compare better than
          angles do. The tool decides which it produced by sending a unit. */}
      {unit === '%' ? (
        <Donut
          // The shared chart knows nothing about tool rows, so the mapping to
          // its slice shape happens here. Colours are left to the categorical
          // palette: a tool group is a bare name, with no colour of its own.
          slices={groups.map((g) => ({ name: str(g.name) ?? '—', value: num(g.count) ?? 0 }))}
          unit={unit}
          // "100% total" in the middle of a share ring says nothing — shares
          // always sum to a hundred. The hours they are a share of do.
          centre={
            num(result.totalHours) !== null
              ? { value: `${Math.round(num(result.totalHours)!)}`, label: 'hours total' }
              : undefined
          }
        />
      ) : (
        <BarPanel groups={groups.slice(0, 12)} unit={unit} />
      )}
    </Shell>
  );
}

function Initials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground">
      {initials}
    </span>
  );
}

function People({ people }: { people: Row[] }) {
  if (people.length === 0) return null;
  return (
    <Shell footer={`${people.length} ${people.length === 1 ? 'person' : 'people'}`}>
      <ul className="space-y-1.5">
        {people.slice(0, 12).map((p, i) => {
          const name = str(p.name) ?? '—';
          const id = str(p.id);
          const sub = [str(p.position), str(p.department)].filter(Boolean).join(' · ');
          const row = (
            <>
              <Initials name={name} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[11px] font-medium text-foreground">{name}</span>
                {sub && <span className="block truncate text-[10px] text-muted-foreground">{sub}</span>}
              </span>
            </>
          );
          return (
            <li key={i}>
              {id ? (
                <Link
                  to="/employees/$id"
                  params={{ id }}
                  className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-muted"
                >
                  {row}
                </Link>
              ) : (
                <span className="flex items-center gap-2 px-1 py-0.5">{row}</span>
              )}
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}

const PRIORITY_TONE: Record<string, string> = {
  urgent: 'bg-destructive/10 text-destructive',
  high: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
};

function Tasks({ tasks }: { tasks: Row[] }) {
  if (tasks.length === 0) return null;
  return (
    <Shell footer={`${tasks.length} ${tasks.length === 1 ? 'task' : 'tasks'}`}>
      <ul className="space-y-1">
        {tasks.slice(0, 12).map((t, i) => {
          const key = str(t.key);
          const boardId = str(t.boardId);
          const priority = str(t.priority);
          const inner = (
            <>
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{key ?? '—'}</span>
              <span className="min-w-0 flex-1 truncate text-[11px] text-foreground">{str(t.title) ?? '—'}</span>
              {priority && priority !== 'none' && (
                <span
                  className={cn(
                    'shrink-0 rounded px-1 py-px text-[9px] font-medium uppercase',
                    PRIORITY_TONE[priority] ?? 'bg-muted text-muted-foreground',
                  )}
                >
                  {priority}
                </span>
              )}
              {str(t.dueDate) && (
                <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">{str(t.dueDate)}</span>
              )}
            </>
          );
          return (
            <li key={i}>
              {key && boardId ? (
                <Link
                  to="/boards"
                  search={{ board: boardId, task: key }}
                  className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-muted"
                >
                  {inner}
                </Link>
              ) : (
                <span className="flex items-center gap-2 px-1 py-1">{inner}</span>
              )}
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}

function Away({ result }: { result: Row }) {
  const absences = (Array.isArray(result.absences) ? result.absences : []) as Row[];
  if (absences.length === 0) return null;
  const range = result.range as Row | undefined;

  return (
    <Shell
      footer={range ? `${str(range.from)} → ${str(range.to)}` : `${absences.length} away`}
    >
      <ul className="space-y-1.5">
        {absences.slice(0, 12).map((a, i) => (
          <li key={i} className="flex items-center gap-2">
            <Initials name={str(a.person) ?? '—'} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[11px] font-medium text-foreground">
                {str(a.person) ?? '—'}
              </span>
              <span className="block truncate text-[10px] text-muted-foreground">
                {str(a.type) ?? 'Away'}
                {num(a.days) !== null && ` · ${num(a.days)} days`}
              </span>
            </span>
            <span className="shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
              {str(a.from)}
              <br />
              {str(a.to)}
            </span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

/** One tool result, if we know how to draw it. */
function Card({ entry }: { entry: AnswerData }) {
  const r = entry.result as Row | Row[] | null;
  if (!r) return null;

  switch (entry.tool) {
    // These three return the same {groupBy, groups:[{name,count}]} shape on
    // purpose: headcount, average pay per department and hours per project are
    // the same picture with a different label under it.
    case 'get_absence_stats':
      return Array.isArray(r) ? null : <MultiChart result={r} />;
    case 'get_headcount':
    case 'get_salary_stats':
    case 'get_time_by_project':
      return Array.isArray(r) ? null : <Headcount result={r} />;
    case 'get_insights':
      return Array.isArray(r) ? null : <Insights result={r} />;
    case 'get_project_money':
      return Array.isArray(r) ? null : <Heatmap result={r} />;
    case 'get_org_chart':
      return Array.isArray(r) ? null : <OrgChart result={r} />;
    case 'search_people':
      return Array.isArray(r) ? <People people={r} /> : null;
    case 'search_tasks':
      return Array.isArray(r) ? <Tasks tasks={r} /> : null;
    case 'who_is_away':
      return Array.isArray(r) ? null : <Away result={r} />;
    case 'get_team': {
      if (Array.isArray(r)) return null;
      const reports = (Array.isArray(r.directReports) ? r.directReports : []) as Row[];
      const peers = (Array.isArray(r.peers) ? r.peers : []) as Row[];
      return <People people={[...reports, ...peers]} />;
    }
    default:
      return null;
  }
}

/** Tools whose results this file knows how to draw. */
const RENDERED = new Set([
  'get_headcount',
  'get_absence_stats',
  'get_salary_stats',
  'get_time_by_project',
  'get_insights',
  'get_project_money',
  'get_org_chart',
  'search_people',
  'search_tasks',
  'who_is_away',
  'get_team',
]);

/**
 * Whether the picture will carry the figures for this answer.
 *
 * The caller uses it to trim the model's prose. Asking the model not to restate
 * a rendered list works most of the time, which is another way of saying it
 * fails sometimes; this decides it on our side instead.
 */
export function hasRenderedData(data?: AnswerData[]): boolean {
  return (data ?? []).some((e) => RENDERED.has(e.tool) && e.result != null);
}

export function AnswerCards({ data }: { data?: AnswerData[] }) {
  if (!data || data.length === 0) return null;
  return (
    <>
      {data.map((entry, i) => (
        <Card key={i} entry={entry} />
      ))}
    </>
  );
}
